import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { MRT_ColumnDef } from 'mantine-react-table';
import React from 'react';
import { formatDate } from 'src/utils';
import { useNavigate } from 'react-router-dom';
import { ChangeTemplateDto } from 'src/api/FiretrolApiClient';
import { useChangeTemplates } from 'src/api/queries/organization/useChangeTemplates';
import { useDeleteChangeTemplate } from 'src/api/queries/changeTemplates/useChangeTemplateMutations';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { Header } from 'src/design/organisms/Header';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';
import { useAuth } from 'src/hooks/useAuth';

export const SettingsChanges: React.FC = () => {
  const navigate = useNavigate();
  const {
    data: templates,
    isLoading,
    isFetching,
    isError,
  } = useChangeTemplates();
  const data = React.useMemo(() => templates ?? [], [templates]);
  const { user } = useAuth();
  const deleteTemplate = useDeleteChangeTemplate();

  const renderOffices = () => {
    if (
      !user ||
      !user.userOrganizationOffice ||
      user.userOrganizationOffice.length === 0
    ) {
      return (
        <Badge variant="light" color="gray">
          None
        </Badge>
      );
    }
    return user.userOrganizationOffice.map((uo) => (
      <Badge key={uo.organizationOffice?.id} variant="light">
        {uo.organizationOffice?.name}
      </Badge>
    ));
  };

  const handleEdit = (row: ChangeTemplateDto) => {
    navigate(`/settings/changes/template/edit/${row.id}`);
  };

  const handleCreate = () => {
    navigate('/settings/changes/template/create');
  };

  const columns = React.useMemo<MRT_ColumnDef<ChangeTemplateDto>[]>(
    () => [
      {
        accessorKey: 'title',
        header: 'Title',
        size: 200,
      },
      {
        accessorKey: 'office.name',
        header: 'Office',
        size: 150,
      },
      {
        id: 'isActive',
        accessorFn: (row) => (row.isActive ? 'Yes' : 'No'),
        header: 'Active',
        size: 100,
      },
      {
        id: 'asOfDate',
        accessorFn: (row) => formatDate(row.asOfDate),
        header: 'As Of',
        size: 120,
      },
      {
        id: 'designSheets',
        accessorFn: (row) => row.designSheets?.length,
        header: 'Design Rate Sheets',
        size: 150,
      },
      {
        id: 'laborSheets',
        accessorFn: (row) => row.laborSheets?.length,
        header: 'Labor Rate Sheets',
        size: 150,
      },
    ],
    [],
  );

  const tableState = useTableState({
    identifier: 'settings-changes-table',
    columns: columns,
    defaultSorting: [{ id: 'title', desc: false }],
  });

  const tableExports = useTableExports<ChangeTemplateDto>(
    'change-templates',
    (data) => {
      return data.map((item) => ({
        ['Title']: item.title,
        ['Office']: item.office?.name || '',
        ['Active']: item.isActive ? 'Yes' : 'No',
        ['As Of']: formatDate(item.asOfDate),
        ['Design Rate Sheets']: item.designSheets?.length || 0,
        ['Labor Rate Sheets']: item.laborSheets?.length || 0,
      }));
    },
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 140 } },
      renderRowActions: ({ row }) => (
        <Group gap="xs">
          <EditActionIcon onClick={() => handleEdit(row.original)} />
          <DeleteActionIcon
            label="Delete Template"
            confirmationText={`Are you sure you want to delete "${row.original.title}"? Templates already used by a job are archived instead.`}
            onDelete={async () => {
              await deleteTemplate.mutateAsync(row.original.id);
            }}
          />
        </Group>
      ),
      enableClickToCopy: false,
      columns: columns,
      data: data,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderTopToolbarCustomActions: () => (
        <Group>
          <CreateButton onClick={handleCreate}>Create Template</CreateButton>
        </Group>
      ),
    },
    getRowId: (row) => String(row.id),
    totalCount: data.length,
    isLoading,
    isFetching,
    isError,
    ...tableExports,
  });

  return (
    <>
      <Header
        title="Changes"
        breadcrumbProps={{
          items: [{ text: 'Settings', href: '/settings' }, { text: 'Changes' }],
        }}
        helpText="Manage change templates for your organization. Change templates define the structure and content for change orders."
      />
      <Stack gap="xs" mb="md">
        <Text size="sm">Your Access:</Text>
        <Group gap="xs">{renderOffices()}</Group>
      </Stack>
      {table}
    </>
  );
};
